import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {LoginInfo} from "~/app/pages/login/login-info";
import {ActivatedRoute, ParamMap} from "@angular/router";
import {UserLoginResult} from "~/app/core/model/user-login-result";
import {InteractionType} from "~/app/core/model/interaction-type";
import {InteractionComponent} from "~/app/core/model/interaction-component";
import {BasePage} from "~/app/pages/base.page";
import {NetworkConnectionType} from "~/app/core/network.service";
import * as Log from "~/app/core/log";
import {CoreService} from "~/app/core/core.service";
import {Title} from "@angular/platform-browser";

@Component({
    selector: "Login",
    templateUrl: "./login.page.html",
    styleUrls: ["./login.page.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginPage extends BasePage implements OnInit, AfterViewInit {
    offerId: number = null;
    loginInfo: LoginInfo = new LoginInfo();
    appleSignInSupported: boolean = false;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly activatedRoute: ActivatedRoute,
        private readonly core: CoreService,
        private readonly title: Title,
    ) {
        super();
        this.title.setTitle('Login');
    }

    ngOnInit(): void {
        this.appleSignInSupported = this.core.platform.is("ios");

        // Init your component properties here.
        // this.core.nav.handleBack(this.page, "/login", (args: any) => {
        //     return () => { this.core.nav.navPageSearching(this.api); };
        // });
        this.activatedRoute.queryParamMap.subscribe((value: ParamMap) => {
            if (value.has("offerId")) {
                this.offerId = Number(value.get("offerId"));
            }
            if (value.has("allowBack")) {
                this.allowBack = value.get("allowBack") === "true";
            }
        });
    }

    ngAfterViewInit(): void {
    }

    showForgotPassword() {
        if (!this.waiting) {
            this.core.nav.navPageForgotPassword(this.offerId, true);
        }
    }

  newUser() {
    if (!this.waiting) {
      this.core.nav.navPageSignup();
    }
  }

    onLoginWithFacebook() {
        if (!this.waiting) {
            if (this.core.net.connectionType === NetworkConnectionType.None) {
                this.handleNoData("login");
            } else {
                this.waitingSpinning = true;
                this.handleLogin(this.core.api.user_loginWithFacebook(), true, InteractionType.LoginFacebook);
            }
        }
    }

    onLoginWithGoogle() {
        if (!this.waiting) {
            if (this.core.net.connectionType === NetworkConnectionType.None) {
                this.handleNoData("login");
            } else {
                this.waitingSpinning = true;
                this.handleLogin(this.core.api.user_loginWithGoogle(), true, InteractionType.LoginGoogle);
            }
        }
    }

    onLoginWithApple() {
        if (!this.waiting) {
            if (this.core.net.connectionType === NetworkConnectionType.None) {
                this.handleNoData("login");
            } else {
                this.waitingSpinning = true;
                this.handleLogin(this.core.api.user_loginWithApple(), true, InteractionType.LoginApple);
            }
        }
    }

    onLogin() {
        if (!this.waiting) {
            if (this.core.net.connectionType === NetworkConnectionType.None) {
                this.handleNoData("login");
            } else {
                this.handleLogin(this.core.api.user_login(this.loginInfo.email, this.loginInfo.password), false, InteractionType.LoginEmail);
            }
        }
    }

    tapPrivacyPolicy(): void {
        if (!this.waiting) {
            this.core.nav.navPagePrivacyPolicy();
        }
    }

    tapTermsOfUse(): void {
        if (!this.waiting) {
            this.core.nav.navPageTermsOfUse();
        }
    }

    private handleLogin(loginResult: Promise<UserLoginResult>, social: boolean, loginType: InteractionType.LoginEmail|InteractionType.LoginFacebook|InteractionType.LoginGoogle|InteractionType.LoginApple) {
        if (!this.waiting) {
            this.waiting = true;
            this.waitingSpinning = true;
            this.cd.detectChanges();
            loginResult.then((result) => {
                Log.debug(Log.Tag.LoginPage, "result: ");
                Log.debug(Log.Tag.LoginPage, result);
                if (result.success) {
                    this.waitingSpinning = false;
                    if (result.validated) {
                        this.waiting = true;
                        this.waitingSpinning = false;
                        this.core.api.interaction(InteractionType.Login, InteractionComponent.LoginPage);
                        this.core.api.interaction(loginType, InteractionComponent.LoginPage);
                        this.core.toast.toastSuccess({
                            header: "Login Success",
                            message: "Thank you. Now enjoy your FREE offers!",
                            onHide: () => {
                                this.core.api.offers_clearSearchResults();
                                // this.core.nav.navPageSearching(this.core.api, this.offerId, false);
                                this.core.nav.navPageHome();
                            }
                        });
                    } else {
                        this.core.nav.navPageValidate(this.offerId, false);
                    }
                } else {
                    this.waiting = false;
                    this.waitingSpinning = true;
                    if (social) {
                        if (loginType === InteractionType.LoginFacebook) this.core.api.interaction(InteractionType.LoginSocialFailFacebook, InteractionComponent.LoginPage);
                        else if (loginType === InteractionType.LoginGoogle) this.core.api.interaction(InteractionType.LoginSocialFailGoogle, InteractionComponent.LoginPage);
                        else if (loginType === InteractionType.LoginApple) this.core.api.interaction(InteractionType.LoginSocialFailApple, InteractionComponent.LoginPage);
                        else this.core.api.interaction(InteractionType.LoginSocialFail, InteractionComponent.LoginPage);
                        // if cancelled from social login, don't show an error
                    } else {
                        this.core.api.interaction(InteractionType.LoginFail, InteractionComponent.LoginPage);
                        this.core.toast.toastError({
                            header: "Unsuccessful Login",
                            message: "Email or password were incorrect. Please verify your account credentials and try again."
                        });
                    }
                }
                this.cd.detectChanges();
            }, (error) => {
                Log.debug(Log.Tag.LoginPage, "ERROR:");
                Log.debug(Log.Tag.LoginPage, error);
                this.waiting = false;
                this.waitingSpinning = true;
                if (error === "AuthError") {
                    this.waitingSpinning = false;
                    this.core.toast.toastError({
                        header: "Invalid Credentials",
                        message: "The email or password you've entered does not match a user in our system. Try again, or use Forgot Password if you cannot remember."
                    });
                } else {
                    this.waiting = false;
                    this.core.toast.toastError({
                        header: "Trouble",
                        message: "Something went wrong while attempting to log in. Check all your info, make sure you have network connectivity, and try again."
                    });
                }
                this.cd.detectChanges();
            });
        }
    }

    private handleNoData(type: "login" | "register") {
        if (type === "login") {
            this.core.toast.toastError({
                header: "No Data",
                message: "Sorry! We can't sign you in without data or wi-fi available.\n\nPlease enable data and try again."
            });
        } else if (type === "register") {
            this.core.toast.toastError({
                header: "No Data",
                message: "Sorry! We can't sign you up without data or wi-fi available.\n\nPlease enable data and try again."
            });
        }
    }
}
